"use client"

import { FC } from "react"
import { Content } from "@prismicio/client"
import {
  PrismicLink,
  PrismicRichText,
  SliceComponentProps,
} from "@prismicio/react"
import { PrismicNextImage } from "@prismicio/next"
import { LazyMotion, domAnimation, m, type Variants } from "motion/react"
import { DecorativeLine } from "@/components/ui/decorative-line"
import { vFadeUp, vStaggerContainer } from "@/lib/animations"

export type SectionIntroTextCardProps =
  SliceComponentProps<Content.SectionIntroTextCardSlice>

const vLineExpand: Variants = {
  hidden: { scaleX: 0, opacity: 0 },
  visible: {
    scaleX: 1,
    opacity: 1,
    transition: { duration: 0.7, ease: "easeOut", delay: 0.25 },
  },
}

const vCardReveal: Variants = {
  hidden: { opacity: 0, y: 52, scale: 0.97 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
  },
}

const vAccentBar: Variants = {
  hidden: { scaleX: 0 },
  visible: {
    scaleX: 1,
    transition: { duration: 0.9, ease: "easeOut", delay: 0.2 },
  },
}

const RING_ORNAMENTS = [
  { top: "6%", left: "3%", size: 44, delay: 0, opacity: 0.35 },
  { top: "18%", right: "4%", size: 28, delay: 0.25, opacity: 0.25 },
  { bottom: "14%", left: "7%", size: 22, delay: 0.5, opacity: 0.2 },
  { bottom: "8%", right: "8%", size: 36, delay: 0.75, opacity: 0.28 },
  { top: "50%", left: "1%", size: 16, delay: 0.4, opacity: 0.18 },
  { top: "40%", right: "2%", size: 20, delay: 0.6, opacity: 0.2 },
] as const

const SectionIntroTextCard: FC<SectionIntroTextCardProps> = ({ slice }) => {
  const { primary } = slice

  return (
    <LazyMotion features={domAnimation}>
      <section
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        aria-label={slice.slice_type
          .split("_")
          .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" ")}
        className="relative overflow-hidden bg-sacred-cream section-py"
      >
        {/* ── Ambient glow blobs ── */}
        <m.div
          aria-hidden
          className="pointer-events-none absolute -top-32 -left-32 size-112 rounded-full bg-sacred-orange/10 blur-3xl"
          initial={{ opacity: 0, scale: 0.75 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.6, ease: "easeOut" }}
          viewport={{ once: true }}
        />
        <m.div
          aria-hidden
          className="pointer-events-none absolute -right-32 -bottom-32 size-96 rounded-full bg-sacred-red/8 blur-3xl"
          initial={{ opacity: 0, scale: 0.75 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.6, ease: "easeOut", delay: 0.3 }}
          viewport={{ once: true }}
        />

        {/* ── Dot-grid texture ── */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-40"
          style={{
            backgroundImage:
              "radial-gradient(circle, oklch(0.65 0.14 60 / 0.18) 1px, transparent 1px)",
            backgroundSize: "30px 30px",
          }}
        />

        {/* ── Floating ring ornaments ── */}
        {RING_ORNAMENTS.map((ring, i) => (
          <m.div
            key={i}
            aria-hidden
            className="pointer-events-none absolute rounded-full border border-sacred-orange"
            style={{
              width: ring.size,
              height: ring.size,
              opacity: ring.opacity,
              top: "top" in ring ? ring.top : undefined,
              bottom: "bottom" in ring ? ring.bottom : undefined,
              left: "left" in ring ? ring.left : undefined,
              right: "right" in ring ? ring.right : undefined,
            }}
            initial={{ opacity: 0, scale: 0, rotate: -45 }}
            whileInView={{ opacity: ring.opacity, scale: 1, rotate: 0 }}
            transition={{
              duration: 0.8,
              delay: ring.delay,
              ease: [0.34, 1.56, 0.64, 1],
            }}
            viewport={{ once: true }}
          />
        ))}

        <div className="relative container flex flex-col items-center gap-8">
          {/* ── Headline ── */}
          {primary.headline && (
            <m.div
              className="flex max-w-3xl flex-col items-center gap-3 text-center"
              variants={vStaggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
            >
              <m.div variants={vFadeUp}>
                <PrismicRichText
                  field={primary.headline}
                  components={{
                    heading2: ({ children }) => (
                      <>
                        <h2 className="mt-6 mb-3 first:mt-0">{children}</h2>
                        <m.div
                          className="flex justify-center"
                          variants={vLineExpand}
                          style={{ transformOrigin: "center" }}
                        >
                          <DecorativeLine className="w-24" />
                        </m.div>
                      </>
                    ),
                    heading3: ({ children }) => (
                      <h3 className="mt-5 mb-3 first:mt-0">{children}</h3>
                    ),
                    heading4: ({ children }) => (
                      <h4 className="mt-4 mb-2 first:mt-0">{children}</h4>
                    ),
                    paragraph: ({ children }) => (
                      <p className="my-4 text-base/relaxed text-gray-700 first:mt-0 last:mb-0">
                        {children}
                      </p>
                    ),
                    listItem: ({ children }) => (
                      <li className="text-base/relaxed text-gray-700">
                        {children}
                      </li>
                    ),
                    oListItem: ({ children }) => (
                      <li className="text-base/relaxed text-gray-700">
                        {children}
                      </li>
                    ),
                    list: ({ children }) => (
                      <ul className="my-4 space-y-1 pl-5">{children}</ul>
                    ),
                    oList: ({ children }) => (
                      <ol className="my-4 space-y-1 pl-5">{children}</ol>
                    ),
                    strong: ({ children }) => (
                      <strong className="font-bold text-sacred-orange">
                        {children}
                      </strong>
                    ),
                    em: ({ children }) => (
                      <em className="text-sacred-red italic">{children}</em>
                    ),
                    image: ({ node }) => (
                      <PrismicNextImage
                        field={node}
                        fallbackAlt=""
                        className="my-4 w-full rounded-lg object-cover"
                        imgixParams={{ auto: ["format", "compress"], q: 75 }}
                      />
                    ),
                    hyperlink: ({ node, children }) => (
                      <PrismicLink
                        field={node.data}
                        className="font-semibold text-sacred-orange underline-offset-2 hover:underline"
                      >
                        {children}
                      </PrismicLink>
                    ),
                  }}
                />
              </m.div>
            </m.div>
          )}

          {/* ── Description card ── */}
          {primary.description && (
            <m.div
              className="w-full max-w-3xl"
              variants={vCardReveal}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
            >
              <div className="overflow-hidden rounded-2xl border border-orange-100 bg-white shadow-md">
                {/* Accent bar sweeps in from left */}
                <m.div
                  className="h-1 w-full bg-linear-to-r from-sacred-orange via-amber-400 to-sacred-red"
                  variants={vAccentBar}
                  style={{ transformOrigin: "left" }}
                />

                <div className="p-8 sm:p-10">
                  <PrismicRichText
                    field={primary.description}
                    components={{
                      heading2: ({ children }) => (
                        <h2 className="mt-6 mb-4 first:mt-0">{children}</h2>
                      ),
                      heading3: ({ children }) => (
                        <h3 className="mt-5 mb-3 first:mt-0">{children}</h3>
                      ),
                      heading4: ({ children }) => (
                        <h4 className="mt-4 mb-2 first:mt-0">{children}</h4>
                      ),
                      paragraph: ({ children }) => (
                        <p className="my-4 text-base/relaxed text-gray-700 first:mt-0 last:mb-0">
                          {children}
                        </p>
                      ),
                      listItem: ({ children }) => (
                        <li className="text-base/relaxed text-gray-700">
                          {children}
                        </li>
                      ),
                      oListItem: ({ children }) => (
                        <li className="text-base/relaxed text-gray-700">
                          {children}
                        </li>
                      ),
                      list: ({ children }) => (
                        <ul className="my-4 space-y-1 pl-5">{children}</ul>
                      ),
                      oList: ({ children }) => (
                        <ol className="my-4 space-y-1 pl-5">{children}</ol>
                      ),
                      strong: ({ children }) => (
                        <strong className="font-bold text-sacred-orange">
                          {children}
                        </strong>
                      ),
                      em: ({ children }) => (
                        <em className="text-sacred-red italic">{children}</em>
                      ),
                      image: ({ node }) => (
                        <PrismicNextImage
                          field={node}
                          fallbackAlt=""
                          className="my-4 w-full rounded-lg object-cover"
                          imgixParams={{ auto: ["format", "compress"], q: 75 }}
                        />
                      ),
                      hyperlink: ({ node, children }) => (
                        <PrismicLink
                          field={node.data}
                          className="font-semibold text-sacred-orange underline-offset-2 hover:underline"
                        >
                          {children}
                        </PrismicLink>
                      ),
                    }}
                  />
                </div>
              </div>
            </m.div>
          )}
        </div>
      </section>
    </LazyMotion>
  )
}

export default SectionIntroTextCard
